import type { FC, PropsWithChildren } from 'react'
import { checkboxClasses, formControlLabelClasses } from '@mui/material'
import { ErrorMessage, Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import {
  Accordion,
  AccordionGroup,
  Body1,
  Body2,
  Box,
  Grid,
  H6,
  Link,
  Stack,
  useIsMobileVariant,
} from '@dhl-ui/components'

import { FormError } from '@dhl/error'
import { CheckboxInput, TextFieldInput } from '@dhl/inputs'
import { AgreedPlacePhotoUploadInput } from '@dhl/interventions'

import type { Sx } from '@dhl/theme'
import { isFeatureEnabled } from '~/config/index.js'
import type { AgreedPlaceInterventionPreferenceWithPhoto } from '~/profile/model/preferences/model.js'

const classes: Record<string, Sx> = {
  title: {
    color: (theme) => theme.palette.grey[400],
    marginBottom: (theme) => theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: (theme) => theme.spacing(1),
    top: (theme) => theme.spacing(1),
    color: (theme) => theme.palette.grey[400],
  },
  smallText: {
    fontSize: '0.9rem',
  },
  agreedPlace: {
    paddingBottom: (theme) => theme.spacing(2),
    width: '100%',
  },
  inputLabel: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '100%',
  },
  radio: {
    '&$checked': {
      color: (theme) => theme.palette.grey[700],
    },
  },
}

type Props = {
  agreedPlace: AgreedPlaceInterventionPreferenceWithPhoto | null
  formId: string
  onChange: (values: AgreedPlaceFormValues) => void
}

export type AgreedPlaceFormValues = {
  agreedPlacePhoto?: Blob
  description: string
  termsAccepted: boolean
}

const PrivacyPolicy = () => {
  const { t } = useTranslation()
  return (
    <Link external href={t('inputs.agreedPlaceInput.privacyPolicyUrl')} rel="noreferrer" target="_blank">
      {t('inputs.agreedPlaceInput.privacyPolicyText')}
    </Link>
  )
}

const TermsConditions = () => {
  const { t } = useTranslation()
  return (
    <Link external href={t('inputs.agreedPlaceInput.termsConditionsUrl')} rel="noreferrer" target="_blank">
      {t('inputs.agreedPlaceInput.termsConditionsText')}
    </Link>
  )
}

const InfoSectionList = ({ children }: PropsWithChildren) => {
  const isMobile = useIsMobileVariant()

  const Component = isMobile ? Body2 : Body1

  return (
    <Component component="ul" sx={{ ml: -3, pr: 2 }}>
      {children}
    </Component>
  )
}

export const AgreedPlaceForm: FC<Props> = ({ agreedPlace, formId, onChange }) => {
  const { t } = useTranslation()
  const isMobileVariant = useIsMobileVariant()

  const initialValues: AgreedPlaceFormValues = {
    agreedPlacePhoto: agreedPlace?.agreedPlacePhoto,
    description: agreedPlace?.description ?? '',
    termsAccepted: !!agreedPlace?.description,
  }

  const agreedPlaceTipsText = t('portal.preferences.agreedPlace.perfectSpotTipsText', {
    returnObjects: true,
  }) as unknown as string[]

  const validationSchema = Yup.object({
    description: Yup.string()
      .required(t('portal.preferences.agreedPlace.inputIsRequiredErrorMessage'))
      .min(5, t('portal.preferences.agreedPlace.errorFieldTooShort', { min: 5 }))
      .max(80, t('portal.preferences.agreedPlace.errorFieldTooLong', { max: 80 })),
    termsAccepted: Yup.boolean()
      .default(false)
      .oneOf([true], t('portal.preferences.agreedPlace.termsAndConditionsRequired')),
  })

  const onSubmit = (values: AgreedPlaceFormValues) => onChange(values)

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      <Form noValidate id={formId}>
        <Stack flexDirection="column" gap={3}>
          <Box>
            <TextFieldInput
              fullWidth
              multiline
              data-test="description-input"
              label={t('portal.preferences.agreedPlace.agreedPlaceLabel')}
              name="description"
              rows="3"
              slotProps={{
                htmlInput: {
                  maxLength: 80,
                },
              }}
              sx={classes.inputLabel}
              type="textarea"
            />
          </Box>

          {isFeatureEnabled('profile.deliveryPreferences.agreedPlacePhotoUpload') && (
            <Grid sx={{ mb: 1 }}>
              <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Grid container direction="column">
                  <Grid container alignItems="center" sx={{ mb: 1 }}>
                    <H6>{t('portal.preferences.agreedPlace.image.title')}</H6>
                  </Grid>

                  <Body2>{t('portal.preferences.agreedPlace.image.description')}</Body2>
                </Grid>
              </Grid>

              <Grid container size={{ xs: 12 }}>
                <AgreedPlacePhotoUploadInput name="agreedPlacePhoto" />
              </Grid>
            </Grid>
          )}

          <AccordionGroup isOutlined isRounded>
            <Accordion hasDivider title={t('portal.preferences.agreedPlace.thingsToMindButton')}>
              <InfoSectionList>
                {(
                  t('portal.preferences.agreedPlace.thingsToRememberText', {
                    returnObjects: true,
                  }) as string[]
                ).map((row, index) => (
                  // eslint-disable-next-line react/no-array-index-key -- we don't have a unique key available
                  <li key={index}>{row}</li>
                ))}
              </InfoSectionList>
            </Accordion>

            <Accordion title={t('portal.preferences.agreedPlace.perfectSpotTipsButton')}>
              <InfoSectionList>
                {agreedPlaceTipsText.map((tip, index) => (
                  // eslint-disable-next-line react/no-array-index-key -- we don't have a unique key available
                  <li key={index}>
                    {tip}
                    {index === agreedPlaceTipsText.length - 1 && (
                      <Link
                        external
                        href={t('portal.preferences.agreedPlace.faqURL')}
                        title="faq"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {t('portal.preferences.agreedPlace.faq')}
                      </Link>
                    )}
                  </li>
                ))}
              </InfoSectionList>
            </Accordion>
          </AccordionGroup>

          <Box
            sx={{
              [`& .${checkboxClasses.root}`]: {
                flexBasis: isMobileVariant ? 48 : 36,
                justifyContent: 'flex-start',
              },
              [`& .${formControlLabelClasses.label}`]: {
                marginLeft: '0 !important',
              },
              [`& .${formControlLabelClasses.root}`]: {
                width: '100%',
              },
            }}
          >
            <CheckboxInput
              required
              label={
                <>
                  {(
                    t('inputs.agreedPlaceInput.privacyAndTermsText', {
                      returnObjects: true,
                    }) as string[]
                  ).map((text, index) => (
                    // eslint-disable-next-line react/no-array-index-key -- we don't have a unique key available
                    <span key={index}>
                      {text} {index === 0 && <TermsConditions />} {index === 1 && <PrivacyPolicy />}
                    </span>
                  ))}
                </>
              }
              name="termsAccepted"
              sx={classes.radio}
            />
            <ErrorMessage name="termsAccepted" component={FormError} />
          </Box>
        </Stack>
      </Form>
    </Formik>
  )
}
